import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import styled from 'styled-components';

const AuthTestContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TestItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const TestForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  input {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  button {
    padding: 0.5rem;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #357abd;
    }
  }
`;

const AuthTest = () => {
  const { signup, login, logout, resetPassword } = useAuth();
  const [testResults, setTestResults] = useState({
    signup: { success: false, error: null },
    login: { success: false, error: null },
    logout: { success: false, error: null },
    resetPassword: { success: false, error: null }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      await signup(email, password);
      setTestResults(prev => ({
        ...prev,
        signup: { success: true, error: null }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        signup: { success: false, error: error.message }
      }));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      setTestResults(prev => ({
        ...prev,
        login: { success: true, error: null }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        login: { success: false, error: error.message }
      }));
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setTestResults(prev => ({
        ...prev,
        logout: { success: true, error: null }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        logout: { success: false, error: error.message }
      }));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await resetPassword(email);
      setTestResults(prev => ({
        ...prev,
        resetPassword: { success: true, error: null }
      }));
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        resetPassword: { success: false, error: error.message }
      }));
    }
  };

  return (
    <AuthTestContainer>
      <TestSection>
        <h3>Authentication Tests</h3>
        
        <TestForm onSubmit={handleSignup}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit">Test Signup</button>
        </TestForm>

        <TestForm onSubmit={handleLogin}>
          <button type="submit">Test Login</button>
        </TestForm>

        <button onClick={handleLogout}>Test Logout</button>

        <TestForm onSubmit={handleResetPassword}>
          <button type="submit">Test Password Reset</button>
        </TestForm>

        <TestResults>
          <h3>Test Results</h3>
          <TestList>
            <TestItem>Signup: {testResults.signup.success ? '✅' : '❌'}</TestItem>
            <TestItem>Login: {testResults.login.success ? '✅' : '❌'}</TestItem>
            <TestItem>Logout: {testResults.logout.success ? '✅' : '❌'}</TestItem>
            <TestItem>Password Reset: {testResults.resetPassword.success ? '✅' : '❌'}</TestItem>
          </TestList>

          {(testResults.signup.error || testResults.login.error || 
            testResults.logout.error || testResults.resetPassword.error) && (
            <ErrorList>
              {testResults.signup.error && <ErrorItem>Signup: {testResults.signup.error}</ErrorItem>}
              {testResults.login.error && <ErrorItem>Login: {testResults.login.error}</ErrorItem>}
              {testResults.logout.error && <ErrorItem>Logout: {testResults.logout.error}</ErrorItem>}
              {testResults.resetPassword.error && <ErrorItem>Password Reset: {testResults.resetPassword.error}</ErrorItem>}
            </ErrorList>
          )}
        </TestResults>
      </TestSection>
    </AuthTestContainer>
  );
};

export default AuthTest; 